// Listing card renderer - uses local /img/cars/ photos
// Each card has an in-card slider showing 4 photos + 1 "Vezi anunțul complet" CTA slide.
// Shape mirrors AP_DATA.listings[i] in src/scripts/data/listings.js

import { num } from '../lib/format.js';
import { t, sprintf } from '../lib/i18n.js';

const SVG_HEART = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7-4.35-9.5-9a5.5 5.5 0 0 1 9.5-5 5.5 5.5 0 0 1 9.5 5C19 16.65 12 21 12 21z"/></svg>';

const SVG_PREV = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
const SVG_NEXT = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
const SVG_CAMERA = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>';

const SVG_CAMERA_LG = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>';

// Mock helper - total photo count per listing (phase 2: real `imageCount` field)
function photoCount(l) {
  return l.imageCount || (12 + (l.id % 6));
}

// Car titles often already end with the year; the card shows the year in its
// own badge, so strip it from the title to avoid showing it twice. Mirrors
// atpl_strip_year_from_title() in inc/helpers.php.
function stripYear(title, year) {
  if (!year) return title;
  return String(title)
    .replace(new RegExp('\\b' + year + '\\b', 'g'), '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s\-–·,/|]+|[\s\-–·,/|]+$/g, '')
    .trim();
}

// Display-only label for transmission. Dataset stores short keys ("Auto",
// "Manual", "CVT", "PDK") for filter matching - render the full word here.
function transLabel(key) {
  if (key === 'Auto') return t('trans_auto', 'Automată');
  if (key === 'Manual') return t('trans_manual', 'Manuală');
  return key;
}

// Grid meta shows the compact transmission form: first 3 letters + a period
// (Automată -> Aut., Mecanică -> Mec., Автомат -> Авт.). Cyrillic-safe: BMP
// chars are single UTF-16 units, so slice(0, 3) counts letters correctly.
function transShort(key) {
  const full = transLabel(key);
  return full.length > 3 ? full.slice(0, 3) + '.' : full;
}

// "No image" placeholder shown when a listing has no usable photo. Prefer the
// URL localised by the theme (AP_DATA.noImage → assets/img/noimage.jpg, kept in
// sync with atpl_card_placeholder_url() in inc/helpers.php); fall back to the
// static prototype path when AP_DATA is absent (standalone html/ preview).
function placeholderImage() {
  try {
    if (typeof AP_DATA !== 'undefined' && AP_DATA && AP_DATA.noImage) return AP_DATA.noImage;
  } catch (e) { /* AP_DATA not defined in the static preview */ }
  return 'assets/img/noimage.jpg';
}

function imageSlides(l) {
  const raw = (l.images && l.images.length ? l.images : [l.image]).filter(Boolean);
  const imgs = (raw.length ? raw : [placeholderImage()]).slice(0, 4);
  // Only the first slide ships with `src` populated - others get `data-src`
  // and are hydrated by card-slider.js when the user navigates to them. This
  // cuts the initial-paint image load from N×4 to N images per card grid.
  return imgs.map((raw, i) => {
    const src = raw || placeholderImage();
    const srcAttr = i === 0 ? `src="${src}"` : `data-src="${src}"`;
    return `
      <div class="ap-card-slide${i === 0 ? ' is-active' : ''}" data-slide="${i}">
        <img ${srcAttr} alt="${l.title}" loading="lazy" decoding="async">
      </div>
    `;
  }).join('');
}

function ctaSlide(l, slideIndex) {
  const total = photoCount(l);
  return `
    <div class="ap-card-slide ap-card-slide--cta" data-slide="${slideIndex}">
      <span class="ap-card-slide__cta-icon" aria-hidden="true">${SVG_CAMERA_LG}</span>
      <span class="ap-card-slide__cta-count">${sprintf(t('photos_in_listing', '%d fotografii în anunț'), total)}</span>
      <span class="ap-card-slide__cta-link">${t('see_all_photos', 'Apasă să le vezi pe toate →')}</span>
    </div>
  `;
}

function dotsHTML(count) {
  return Array.from({ length: count }, (_, i) => `
    <button class="ap-card-dot${i === 0 ? ' is-active' : ''}" type="button" data-dot="${i}" aria-label="Slide ${i + 1}"></button>
  `).join('');
}

function photoSlider(l) {
  const imgCount = Math.min(4, (l.images || []).length || 1);
  const totalSlides = imgCount + 1; // 4 images + 1 CTA
  return `
    <div class="ap-listing-card__photo" data-component="card-slider" data-active="0" data-count="${totalSlides}">
      <a href="listing.html?id=${l.id}" class="ap-listing-card__track-link" aria-label="${l.title}">
        <div class="ap-listing-card__track">
          ${imageSlides(l)}
          ${ctaSlide(l, imgCount)}
        </div>
      </a>
      <div class="ap-listing-card__pills">{{TAG}}</div>
      <button class="ap-listing-card__fav{{FAV}}" type="button" aria-label="${t('fav_add', 'Adaugă la favorite')}" data-action="favorite" data-id="${l.id}">
        ${SVG_HEART}
      </button>
      <button class="ap-listing-card__nav ap-listing-card__nav--prev" type="button" data-action="card-prev" aria-label="${t('card_prev', 'Anterior')}">${SVG_PREV}</button>
      <button class="ap-listing-card__nav ap-listing-card__nav--next" type="button" data-action="card-next" aria-label="${t('card_next', 'Următor')}">${SVG_NEXT}</button>
      <div class="ap-listing-card__dots">${dotsHTML(totalSlides)}</div>
      <span class="ap-listing-card__counter">${SVG_CAMERA}<span data-role="card-counter">1</span> / ${totalSlides}</span>
    </div>
  `;
}

// Cap stagger delay so later pages don't keep getting slower
function staggerDelay(i) {
  return Math.min(i, 7) * 50;
}

export function listingCardHTML(l, opts = {}) {
  const featured = !!l.featured;
  const tag = l.tag ? `<span class="ap-pill ${featured ? 'ap-pill--red' : ''}">${l.tag}</span>` : '';
  const featuredPill = featured ? `<span class="ap-pill ap-pill--red">${t('featured', 'Recomandată')}</span>` : '';
  const fav = opts.favorited ? ' is-fav' : '';
  const photo = photoSlider(l).replace('{{TAG}}', tag + featuredPill).replace('{{FAV}}', fav);
  const animAttrs = `data-anim="rise" style="--ap-delay:${staggerDelay(opts.index || 0)}"`;

  return `
    <article class="ap-listing-card" data-id="${l.id}" ${animAttrs}>
      ${photo}
      <div class="ap-listing-card__body">
        <div class="ap-listing-card__title-row">
          <div class="ap-listing-card__title-wrap">
            <a href="listing.html?id=${l.id}" class="ap-listing-card__title">${stripYear(l.title, l.year)}</a>
          </div>
          <div class="ap-listing-card__price-block">
            <div class="ap-listing-card__pricebox">${l.oldPrice && l.oldPrice > l.price ? `<div class="ap-listing-card__oldprice">${num(l.oldPrice)} €</div>` : ''}<div class="ap-listing-card__monthly">${num(l.price)} €</div></div>
            <div class="ap-listing-card__price">${l.monthly} ${t('per_month', '€/lună')}</div>
          </div>
        </div>
        <div class="ap-listing-card__meta">
          <div class="ap-listing-card__meta-row">
            ${l.year ? `<span class="ap-listing-card__meta-item">${l.year}</span>` : ''}
            <span class="ap-listing-card__meta-item">${num(l.miles)} km</span>
            ${l.engine ? `<span class="ap-listing-card__meta-item">${l.engine}</span>` : ''}
            <span class="ap-listing-card__meta-item">${transShort(l.trans)}</span>
          </div>
          <div class="ap-listing-card__meta-row">
            <span class="ap-listing-card__meta-item">${l.fuel}</span>
            <span class="ap-listing-card__meta-item">${l.location || l.brand}</span>
          </div>
        </div>
      </div>
    </article>
  `;
}

export function listingRowHTML(l, opts = {}) {
  // Row (list) view shares the same in-card slider as the grid view.
  const featured = !!l.featured;
  const tag = l.tag ? `<span class="ap-pill ${featured ? 'ap-pill--red' : ''}">${l.tag}</span>` : '';
  const featuredPill = featured ? `<span class="ap-pill ap-pill--red">${t('featured', 'Recomandată')}</span>` : '';
  const fav = opts.favorited ? ' is-fav' : '';
  const photo = photoSlider(l).replace('{{TAG}}', tag + featuredPill).replace('{{FAV}}', fav);
  const animAttrs = `data-anim="rise" style="--ap-delay:${staggerDelay(opts.index || 0)}"`;

  return `
    <article class="ap-listing-row" data-id="${l.id}" ${animAttrs}>
      ${photo}
      <div class="ap-listing-row__info">
        <div class="ap-listing-row__header">
          <div class="ap-listing-row__title-block">
            <div class="ap-listing-row__title-line">
              <a href="listing.html?id=${l.id}" class="ap-listing-card__title">${stripYear(l.title, l.year)}</a>
              <span class="ap-listing-row__year-badge">${l.year}</span>
            </div>
            <div class="ap-listing-row__meta">
              <span class="ap-listing-card__meta-item">${num(l.miles)} km</span>
              ${l.engine ? `<span class="ap-listing-card__meta-item">${l.engine}</span>` : ''}
              <span class="ap-listing-card__meta-item">${transShort(l.trans)}</span>
              <span class="ap-listing-card__meta-item">${l.fuel}</span>
              <span class="ap-listing-card__meta-item">${l.location || l.brand}</span>
            </div>
          </div>
          <div class="ap-listing-row__price-block">
            <div class="ap-listing-card__pricebox">${l.oldPrice && l.oldPrice > l.price ? `<div class="ap-listing-card__oldprice">${num(l.oldPrice)} €</div>` : ''}<div class="ap-listing-card__monthly">${num(l.price)} €</div></div>
            <div class="ap-listing-row__price">${l.monthly} ${t('per_month', '€/lună')}</div>
            <a href="listing.html?id=${l.id}" class="ap-btn ap-btn--outline ap-btn--sm ap-listing-row__cta">${t('details', 'Detalii')}</a>
          </div>
        </div>
      </div>
    </article>
  `;
}

export function renderListingsInto(container, listings, opts = {}) {
  if (!container) return;
  // Honor server-rendered grids: the WordPress theme paints listing-card
  // HTML directly into the container (for SEO + first paint). We skip the
  // first JS render so server output isn't clobbered. The attribute is
  // removed so subsequent renders (filter changes, sort, pagination)
  // proceed normally - only the initial paint is protected.
  if (container.dataset.serverRendered === '1') {
    delete container.dataset.serverRendered;
    return;
  }
  const favs = opts.favs || new Set();
  const view = opts.view || 'grid';
  const useRow = view === 'list';
  const renderer = useRow ? listingRowHTML : listingCardHTML;
  container.innerHTML = listings
    .map((l, i) => {
      const card = renderer(l, { favorited: favs.has(l.id), index: i });
      if (opts.banner && i === opts.banner.afterIndex) {
        return card + opts.banner.html;
      }
      return card;
    }).join('');
}

// Replace listings grid with server-rendered HTML (used by the WP REST
// flow). Mirrors renderListingsInto's behaviour but takes pre-rendered
// markup instead of objects.
export function renderListingsHTMLInto(container, html, opts = {}) {
  if (!container) return;
  delete container.dataset.serverRendered;
  container.innerHTML = html || '';
}
